import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../services/api';

const STATUS_ORDER  = ['lead', 'verified', 'visa_approved', 'enrolled'];
const STATUS_NEXT   = { lead: 'verified', verified: 'visa_approved', visa_approved: 'enrolled' };
const STATUS_LABEL  = { lead: 'Lead', verified: 'Verified', visa_approved: 'Visa Approved', enrolled: 'Enrolled' };
const REWARD_COLOR  = { pending: '#f59e0b', approved: '#3b82f6', paid: '#10b981' };
const BENE_LABEL    = { referrer: 'Referral Reward', referred: 'Enrollment Bonus' };
const BENE_COLOR    = { referrer: { bg: '#dbeafe', text: '#1d4ed8' }, referred: { bg: '#dcfce7', text: '#166534' } };
const ROLE_STYLE    = {
  referrer:   { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  referred:   { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
  councillor: { bg: '#fdf4ff', color: '#7c3aed', border: '#e9d5ff' },
  admin:      { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
};
const BLANK_USER = { name: '', email: '', phone: '', password: '', role: 'councillor' };


export default function AdminDashboardPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tab, setTab]           = useState(searchParams.get('tab') || 'summary');
  const [studentRole, setStudentRole] = useState(searchParams.get('role') || '');

  useEffect(() => {
    setTab(searchParams.get('tab') || 'summary');
    setStudentRole(searchParams.get('role') || '');
  }, [searchParams]);
  const [referrals, setReferrals]         = useState([]);
  const [rewards, setRewards]             = useState([]);
  const [students, setStudents]           = useState([]);
  const [councillors, setCouncillors]     = useState([]);
  const [users, setUsers]                 = useState([]);
  const [summary, setSummary]             = useState(null);
  const [statusFilter, setStatusFilter]   = useState('');
  const [rewardFilter, setRewardFilter]   = useState('');
  const [loading, setLoading]             = useState(true);
  const [actionLoading, setAL]            = useState(null);
  const [error, setError]                 = useState('');
  const [referralSearch, setReferralSearch] = useState('');
  const [userSearch, setUserSearch]         = useState('');
  const [userForm, setUserForm]           = useState(BLANK_USER);
  const [showUserForm, setShowUserForm]   = useState(false);
  const [userFormErr, setUserFormErr]     = useState('');
  const [userFormOk, setUserFormOk]       = useState('');
  const [creating, setCreating]           = useState(false);
  const [editModal, setEditModal]         = useState(null);
  const [editForm, setEditForm]           = useState({ name: '', email: '', phone: '' });
  const [editSaving, setEditSaving]       = useState(false);
  const [pwModal, setPwModal]             = useState(null);
  const [pwForm, setPwForm]               = useState({ newPassword: '', confirm: '' });
  const [pwSaving, setPwSaving]           = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      if (tab === 'summary') {
        const { data } = await api.get('/referral/admin/summary');
        setSummary(data);
      } else if (tab === 'referrals') {
        const url = statusFilter ? `/referral/admin/referrals?status=${statusFilter}` : '/referral/admin/referrals';
        const { data } = await api.get(url);
        setReferrals(data);
      } else if (tab === 'rewards') {
        const url = rewardFilter ? `/referral/admin/rewards?status=${rewardFilter}` : '/referral/admin/rewards';
        const { data } = await api.get(url);
        setRewards(data);
      } else if (tab === 'students') {
        const { data } = await api.get('/referral/admin/students');
        setStudents(data);
      } else if (tab === 'councillors') {
        const { data } = await api.get('/referral/admin/councillors');
        setCouncillors(data);
      } else if (tab === 'users') {
        const { data } = await api.get('/referral/admin/students');
        setUsers(data);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load data');
    } finally { setLoading(false); }
  }, [tab, statusFilter, rewardFilter]);

  useEffect(() => { load(); }, [load]);

  const advanceStatus = async (refId, newStatus) => {
    setAL(refId);
    try {
      await api.put(`/referral/admin/referrals/${refId}/status`, { status: newStatus });
      await load();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setAL(null); }
  };

  const approveReward = async (id) => {
    setAL(id);
    try { await api.put(`/referral/admin/rewards/${id}/approve`); await load(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setAL(null); }
  };

  const markPaid = async (id) => {
    setAL(id);
    try { await api.put(`/referral/admin/rewards/${id}/mark-paid`); await load(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setAL(null); }
  };

  const toggleActive = async (id, current) => {
    setAL(id);
    try {
      await api.put(`/referral/admin/students/${id}/toggle-active`);
      const upd = u => u.id === id ? { ...u, is_active: !current } : u;
      setStudents(prev => prev.map(upd));
      setUsers(prev => prev.map(upd));
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    finally { setAL(null); }
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      await api.put(`/referral/admin/users/${id}/role`, { role: newRole });
      setUsers(us => us.map(u => u.id === id ? { ...u, role: newRole } : u));
    } catch (e) { alert(e.response?.data?.detail || 'Failed to update role'); }
  };

  const handleDeleteStudent = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/referral/admin/users/${id}`);
      setStudents(prev => prev.filter(s => s.id !== id));
    } catch (e) { alert(e.response?.data?.detail || 'Failed to delete'); }
  };

  const handleDeleteUser = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/referral/admin/users/${id}`);
      setUsers(us => us.filter(u => u.id !== id));
    } catch (e) { alert(e.response?.data?.detail || 'Failed to delete'); }
  };

  const handleCreateUser = async e => {
    e.preventDefault(); setUserFormErr(''); setUserFormOk('');
    if (!userForm.name || !userForm.email || !userForm.phone || !userForm.password) {
      setUserFormErr('All fields are required.'); return;
    }
    setCreating(true);
    try {
      await api.post('/referral/admin/users', userForm);
      setUserFormOk(`Account "${userForm.name}" (${userForm.role}) created.`);
      setUserForm(BLANK_USER); setShowUserForm(false);
      load();
    } catch (e) { setUserFormErr(e.response?.data?.detail || 'Failed to create'); }
    finally { setCreating(false); }
  };

  const openEdit = (u) => { setEditForm({ name: u.name, email: u.email, phone: u.phone }); setEditModal(u); };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await api.put(`/referral/admin/users/${editModal.id}`, editForm);
      setUsers(us => us.map(u => u.id === editModal.id ? { ...u, ...editForm } : u));
      setEditModal(null);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to save'); }
    finally { setEditSaving(false); }
  };

  const openPw = (u) => { setPwForm({ newPassword: '', confirm: '' }); setPwModal(u); };

  const savePassword = async () => {
    if (pwForm.newPassword !== pwForm.confirm) { alert('Passwords do not match'); return; }
    if (pwForm.newPassword.length < 6) { alert('Password must be at least 6 characters'); return; }
    setPwSaving(true);
    try {
      await api.put(`/referral/admin/users/${pwModal.id}/password`, { new_password: pwForm.newPassword });
      setPwModal(null);
    } catch (e) { alert(e.response?.data?.detail || 'Failed to change password'); }
    finally { setPwSaving(false); }
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  const fmtINR  = n => `₹${Number(n).toLocaleString('en-IN')}`;

  return (
    <div style={S.page}>
      <div style={S.pageHeader}>
        <h1 style={S.pageTitle}>
          {tab === 'summary'        && '📊 Summary'}
          {tab === 'referrals'      && '🔗 Referrals'}
          {tab === 'rewards'        && '💰 Rewards'}
          {tab === 'students'       && '👥 Students'}
          {tab === 'councillors'    && '🏛 Councillors'}
          {tab === 'users'          && '👤 Manage Users'}
        </h1>
      </div>

      {error && <div style={S.errBox}>{error}</div>}

      {/* ── Summary ── */}
      {tab === 'summary' && !loading && summary && (
        <div style={S.summaryGrid}>
          {[
            { label: 'Referral Submissions', value: summary.referral_submissions, color: '#7c3aed', tab: 'referrals' },
            { label: 'Referred Students',    value: summary.referred_students,    color: '#0891b2', tab: 'students', role: 'referred' },
            { label: 'Pending Verification', value: summary.pending_verification, color: '#f97316', tab: 'referrals' },
            { label: 'Total Councillors',    value: summary.total_councillors,    color: '#6d28d9', tab: 'councillors' },
            { label: 'Pending Payouts',      value: fmtINR(summary.total_pending),color: '#f59e0b', tab: 'rewards' },
            { label: 'Approved Rewards',     value: fmtINR(summary.total_approved),color: '#3b82f6', tab: 'rewards' },
            { label: 'Total Paid Out',       value: fmtINR(summary.total_paid),   color: '#10b981', tab: 'rewards' },
          ].map(c => (
            <div
              key={c.label}
              style={{ ...S.summaryCard, borderLeftColor: c.color, cursor: 'pointer' }}
              onClick={() => navigate(`/admin?tab=${c.tab}${c.role ? `&role=${c.role}` : ''}`)}
            >
              <div style={S.summaryVal}>{c.value}</div>
              <div style={S.summaryLabel}>{c.label}</div>
              <div style={{ fontSize: '.72rem', color: '#94a3b8', marginTop: '.4rem' }}>Click to view →</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Referrals ── */}
      {tab === 'referrals' && (
        <>
          <div style={S.filterRow}>
            <label style={S.filterLabel}>Status:</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={S.select}>
              <option value="">All</option>
              {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
            </select>
            <input
              type="text"
              placeholder="Search referrer or referred…"
              value={referralSearch}
              onChange={e => setReferralSearch(e.target.value)}
              style={S.searchInput}
            />
            <span style={S.count}>
              {referrals.filter(r => {
                const q = referralSearch.toLowerCase();
                return !q || [r.referrer_name, r.referrer_email, r.referred_name, r.referred_email, r.referred_phone].some(v => v?.toLowerCase().includes(q));
              }).length} of {referrals.length} referrals
            </span>
          </div>
          {loading ? <Loader /> : (
            <TableWrap>
              <thead><tr style={S.thead}>
                {['Referrer','Referred Student','Status','Rewards','Date','Action'].map(h => <Th key={h}>{h}</Th>)}
              </tr></thead>
              <tbody>
                {referrals.length === 0 && <EmptyRow cols={6} />}
                {referrals.filter(r => {
                  const q = referralSearch.toLowerCase();
                  return !q || [r.referrer_name, r.referrer_email, r.referred_name, r.referred_email, r.referred_phone].some(v => v?.toLowerCase().includes(q));
                }).length === 0 && referrals.length > 0 && <EmptyRow cols={6} />}
                {referrals.filter(r => {
                  const q = referralSearch.toLowerCase();
                  return !q || [r.referrer_name, r.referrer_email, r.referred_name, r.referred_email, r.referred_phone].some(v => v?.toLowerCase().includes(q));
                }).map(ref => {
                  const next = STATUS_NEXT[ref.status];
                  return (
                    <tr key={ref.id} style={S.tr}>
                      <Td><b>{ref.referrer_name}</b><br/><small style={S.small}>{ref.referrer_email}</small></Td>
                      <Td><b>{ref.referred_name}</b><br/><small style={S.small}>{ref.referred_email}</small><br/><small style={S.small}>{ref.referred_phone}</small></Td>
                      <Td><StatusBadge status={ref.status} /></Td>
                      <Td>{ref.rewards?.map((rw, i) => (
                        <div key={i} style={{ fontSize: '.78rem', display: 'flex', gap: '.3rem', marginBottom: '.2rem' }}>
                          <span style={{ color: REWARD_COLOR[rw.status] }}>●</span>
                          <span>₹{rw.amount.toLocaleString()}</span>
                          <span style={{ color: '#94a3b8' }}>{rw.stage.replace('_',' ')} · {rw.status}</span>
                        </div>
                      )) || '—'}</Td>
                      <Td><small style={S.small}>{fmtDate(ref.created_at)}</small></Td>
                      <Td>
                        {next
                          ? <button style={S.blueBtn} disabled={actionLoading === ref.id} onClick={() => advanceStatus(ref.id, next)}>
                              {actionLoading === ref.id ? '…' : `Mark ${STATUS_LABEL[next]}`}
                            </button>
                          : <span style={S.doneLabel}>✓ Complete</span>}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </TableWrap>
          )}
        </>
      )}

      {/* ── Rewards ── */}
      {tab === 'rewards' && (
        <>
          <div style={S.filterRow}>
            <label style={S.filterLabel}>Filter:</label>
            <select value={rewardFilter} onChange={e => setRewardFilter(e.target.value)} style={S.select}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
            </select>
            <span style={S.count}>{rewards.length} {rewards.length === 1 ? 'reward' : 'rewards'}</span>
          </div>
          {loading ? <Loader /> : (
            <TableWrap>
              <thead><tr style={S.thead}>
                {['Recipient','Type','Milestone','Amount','Referrer → Referred','Status','Date','Paid On','Actions'].map(h => <Th key={h}>{h}</Th>)}
              </tr></thead>
              <tbody>
                {rewards.length === 0 && <EmptyRow cols={9} />}
                {rewards.map(rw => {
                  const bc = BENE_COLOR[rw.beneficiary] || BENE_COLOR.referrer;
                  return (
                    <tr key={rw.id} style={S.tr}>
                      <Td><b>{rw.recipient_name || '—'}</b><br/><small style={S.small}>{rw.recipient_email}</small></Td>
                      <Td><span style={{ ...S.badge, background: bc.bg, color: bc.text }}>{BENE_LABEL[rw.beneficiary]}</span></Td>
                      <Td><StageBadge stage={rw.stage} /></Td>
                      <Td><b>{fmtINR(rw.amount)}</b></Td>
                      <Td><small style={S.small}>{rw.referrer_name} → {rw.referred_name}</small></Td>
                      <Td><span style={{ color: REWARD_COLOR[rw.status], fontWeight: 600 }}>
                        {rw.status === 'pending' ? '⏳' : rw.status === 'approved' ? '✅' : '💰'} {rw.status}
                      </span></Td>
                      <Td><small style={S.small}>{fmtDate(rw.created_at)}</small></Td>
                      <Td><small style={S.small}>{fmtDate(rw.paid_at)}</small></Td>
                      <Td>
                        {rw.status === 'pending'  && <button style={S.blueBtn}  disabled={actionLoading === rw.id} onClick={() => approveReward(rw.id)}>{actionLoading === rw.id ? '…' : 'Approve'}</button>}
                        {rw.status === 'approved' && <button style={S.greenBtn} disabled={actionLoading === rw.id} onClick={() => markPaid(rw.id)}>{actionLoading === rw.id ? '…' : 'Mark Paid'}</button>}
                        {rw.status === 'paid'     && <span style={S.doneLabel}>✓ Paid</span>}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </TableWrap>
          )}
        </>
      )}

      {/* ── Students ── */}
      {tab === 'students' && (
        loading ? <Loader /> : (
          <>
            <div style={S.filterRow}>
              <label style={S.filterLabel}>Role:</label>
              <select value={studentRole} onChange={e => navigate(`/admin?tab=students${e.target.value ? `&role=${e.target.value}` : ''}`)} style={S.select}>
                <option value="">All Students</option>
                <option value="referrer">Referrers only</option>
                <option value="referred">Referred only</option>
              </select>
              <span style={S.count}>{students.filter(s => !studentRole || s.role === studentRole).length} of {students.length} students</span>
            </div>
          <TableWrap>
            <thead><tr style={S.thead}>
              {['Name','Email','Phone','Role','Referred By','Joined','Status','Actions'].map(h => <Th key={h}>{h}</Th>)}
            </tr></thead>
            <tbody>
              {students.filter(s => !studentRole || s.role === studentRole).length === 0 && <EmptyRow cols={8} />}
              {students.filter(s => !studentRole || s.role === studentRole).map(s => (
                <tr key={s.id} style={{ ...S.tr, opacity: s.is_active ? 1 : 0.6 }}>
                  <Td><b>{s.name}</b></Td>
                  <Td><small style={S.small}>{s.email}</small></Td>
                  <Td><small style={S.small}>{s.phone}</small></Td>
                  <Td><span style={{ ...S.badge, background: s.role === 'referred' ? '#e0f2fe' : '#eff6ff', color: s.role === 'referred' ? '#0369a1' : '#1d4ed8' }}>{s.role}</span></Td>
                  <Td><small style={S.small}>{s.referred_by_id ? `#${s.referred_by_id}` : '—'}</small></Td>
                  <Td><small style={S.small}>{fmtDate(s.created_at)}</small></Td>
                  <Td>
                    <span style={{ ...S.badge, background: s.is_active ? '#dcfce7' : '#fee2e2', color: s.is_active ? '#166534' : '#991b1b' }}>
                      {s.is_active ? '● Active' : '● Disabled'}
                    </span>
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                      <button
                        style={s.is_active ? S.redBtn : S.greenBtn}
                        disabled={actionLoading === s.id}
                        onClick={() => toggleActive(s.id, s.is_active)}
                      >
                        {actionLoading === s.id ? '…' : s.is_active ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        style={S.deleteBtn}
                        onClick={() => handleDeleteStudent(s.id, s.name)}
                      >
                        Delete
                      </button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
          </>
        )
      )}

      {/* ── Councillors ── */}
      {tab === 'councillors' && (
        loading ? <Loader /> : (
          <TableWrap>
            <thead><tr style={S.thead}>
              {['Name','Email','Phone','Joined','Status','Actions'].map(h => <Th key={h}>{h}</Th>)}
            </tr></thead>
            <tbody>
              {councillors.length === 0 && <EmptyRow cols={6} />}
              {councillors.map(c => (
                <tr key={c.id} style={{ ...S.tr, opacity: c.is_active ? 1 : 0.6 }}>
                  <Td><b>{c.name}</b></Td>
                  <Td><small style={S.small}>{c.email}</small></Td>
                  <Td><small style={S.small}>{c.phone}</small></Td>
                  <Td><small style={S.small}>{fmtDate(c.created_at)}</small></Td>
                  <Td>
                    <span style={{ ...S.badge, background: c.is_active ? '#dcfce7' : '#fee2e2', color: c.is_active ? '#166534' : '#991b1b' }}>
                      {c.is_active ? '● Active' : '● Disabled'}
                    </span>
                  </Td>
                  <Td>
                    <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                      <button
                        style={c.is_active ? S.redBtn : S.greenBtn}
                        disabled={actionLoading === c.id}
                        onClick={() => toggleActive(c.id, c.is_active)}
                      >
                        {actionLoading === c.id ? '…' : c.is_active ? 'Disable' : 'Enable'}
                      </button>
                      <button style={S.deleteBtn} onClick={() => handleDeleteStudent(c.id, c.name)}>Delete</button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )
      )}

      {/* ── Manage Users ── */}
      {tab === 'users' && (
        <>
          <div style={S.filterRow}>
            <button style={S.createBtn} onClick={() => { setShowUserForm(f => !f); setUserFormErr(''); setUserFormOk(''); }}>
              {showUserForm ? '✕ Cancel' : '+ Create User'}
            </button>
            <input
              type="text"
              placeholder="Search by name, email or phone…"
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              style={S.searchInput}
            />
            <span style={S.count}>{users.filter(u => {
              const q = userSearch.toLowerCase();
              return !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.phone.includes(q);
            }).length} of {users.length} accounts</span>
          </div>

          {showUserForm && (
            <div style={S.formCard}>
              <h2 style={S.formTitle}>New User / Admin Account</h2>
              <form onSubmit={handleCreateUser}>
                <div style={S.formRow}>
                  <div style={S.field}><label style={S.label}>Full Name</label><input name="name" value={userForm.name} onChange={e => setUserForm(f => ({ ...f, name: e.target.value }))} style={S.input} placeholder="Jane Smith" required /></div>
                  <div style={S.field}><label style={S.label}>Email</label><input name="email" type="email" value={userForm.email} onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} style={S.input} placeholder="jane@example.com" required /></div>
                </div>
                <div style={S.formRow}>
                  <div style={S.field}><label style={S.label}>Phone (with country code)</label><input name="phone" value={userForm.phone} onChange={e => setUserForm(f => ({ ...f, phone: e.target.value }))} style={S.input} placeholder="919876543210" required /></div>
                  <div style={S.field}><label style={S.label}>Password</label><input name="password" type="password" value={userForm.password} onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))} style={S.input} placeholder="Min 6 characters" required /></div>
                </div>
                <div style={{ ...S.field, maxWidth: 240, marginBottom: '1rem' }}>
                  <label style={S.label}>Role</label>
                  <select value={userForm.role} onChange={e => setUserForm(f => ({ ...f, role: e.target.value }))} style={S.input}>
                    <option value="referrer">Referrer (refers others)</option>
                    <option value="referred">Referred (referred student)</option>
                    <option value="councillor">Councillor (view referrals)</option>
                    <option value="admin">Admin (full access)</option>
                  </select>
                </div>
                {userFormErr && <div style={S.errBox}>{userFormErr}</div>}
                {userFormOk  && <div style={S.okBox}>{userFormOk}</div>}
                <button type="submit" style={S.createBtn} disabled={creating}>{creating ? 'Creating…' : 'Create Account'}</button>
              </form>
            </div>
          )}

          {userFormOk && !showUserForm && <div style={S.okBox}>{userFormOk}</div>}
          {loading ? <Loader /> : (
            <TableWrap>
              <thead><tr style={S.thead}>
                {['Name','Email','Phone','Role','Status','Actions'].map(h => <Th key={h}>{h}</Th>)}
              </tr></thead>
              <tbody>
                {users.length === 0 && <EmptyRow cols={6} />}
                {users.filter(u => {
                  const q = userSearch.toLowerCase();
                  return !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.phone.includes(q);
                }).length === 0 && users.length > 0 && <EmptyRow cols={6} />}
                {users.filter(u => {
                  const q = userSearch.toLowerCase();
                  return !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.phone.includes(q);
                }).map((u, i) => {
                  const rs = ROLE_STYLE[u.role] || ROLE_STYLE.referral;
                  return (
                    <tr key={u.id} style={{ ...S.tr, opacity: u.is_active ? 1 : 0.6 }}>
                      <Td><b>{u.name}</b><br/><small style={S.small}>#{u.id}</small></Td>
                      <Td><small style={S.small}>{u.email}</small></Td>
                      <Td><small style={S.small}>{u.phone}</small></Td>
                      <Td>
                        <select
                          value={u.role}
                          onChange={e => handleRoleChange(u.id, e.target.value)}
                          style={{ padding: '.25rem .6rem', borderRadius: 6, fontSize: '.78rem', fontWeight: 700, cursor: 'pointer', background: rs.bg, color: rs.color, border: `1px solid ${rs.border}` }}
                        >
                          <option value="referrer">Referrer</option>
                          <option value="referred">Referred</option>
                          <option value="councillor">Councillor</option>
                          <option value="admin">Admin</option>
                        </select>
                      </Td>
                      <Td>
                        <span style={{ ...S.badge, background: u.is_active ? '#dcfce7' : '#fee2e2', color: u.is_active ? '#166534' : '#991b1b' }}>
                          {u.is_active ? 'Active' : 'Disabled'}
                        </span>
                      </Td>
                      <Td>
                        <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
                          <button style={S.editBtn} onClick={() => openEdit(u)}>Edit</button>
                          <button style={S.pwBtn} onClick={() => openPw(u)}>Password</button>
                          <button style={u.is_active ? S.redBtn : S.greenBtn} disabled={actionLoading === u.id} onClick={() => toggleActive(u.id, u.is_active)}>
                            {actionLoading === u.id ? '…' : u.is_active ? 'Disable' : 'Enable'}
                          </button>
                          <button style={S.deleteBtn} onClick={() => handleDeleteUser(u.id, u.name)}>Delete</button>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </TableWrap>
          )}
        </>
      )}
      {/* ── Edit User Modal ── */}
      {editModal && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <h3 style={S.modalTitle}>Edit User — {editModal.name}</h3>
            {[
              { label: 'Full Name', key: 'name', type: 'text' },
              { label: 'Email',     key: 'email', type: 'email' },
              { label: 'Phone',     key: 'phone', type: 'tel' },
            ].map(({ label, key, type }) => (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <label style={S.label}>{label}</label>
                <input type={type} value={editForm[key]} onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))} style={S.input} />
              </div>
            ))}
            <div style={S.modalFooter}>
              <button onClick={() => setEditModal(null)} style={S.cancelBtn}>Cancel</button>
              <button onClick={saveEdit} disabled={editSaving} style={S.editBtn}>{editSaving ? 'Saving…' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Change Password Modal ── */}
      {pwModal && (
        <div style={S.overlay}>
          <div style={S.modal}>
            <h3 style={S.modalTitle}>Change Password — {pwModal.name}</h3>
            {[
              { label: 'New Password',     key: 'newPassword' },
              { label: 'Confirm Password', key: 'confirm' },
            ].map(({ label, key }) => (
              <div key={key} style={{ marginBottom: '1rem' }}>
                <label style={S.label}>{label}</label>
                <input type="password" value={pwForm[key]} onChange={e => setPwForm(f => ({ ...f, [key]: e.target.value }))} style={S.input} />
              </div>
            ))}
            <div style={S.modalFooter}>
              <button onClick={() => setPwModal(null)} style={S.cancelBtn}>Cancel</button>
              <button onClick={savePassword} disabled={pwSaving} style={S.pwBtn}>{pwSaving ? 'Saving…' : 'Update Password'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Small components ──────────────────────────────────────────────────────────

function TableWrap({ children }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,.08)', overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.88rem' }}>{children}</table>
    </div>
  );
}
const Th = ({ children }) => (
  <th style={{ padding: '.7rem 1rem', textAlign: 'left', color: '#64748b', fontSize: '.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{children}</th>
);
const Td = ({ children }) => (
  <td style={{ padding: '.8rem 1rem', verticalAlign: 'middle', borderBottom: '1px solid #f1f5f9' }}>{children}</td>
);
const EmptyRow = ({ cols }) => (
  <tr><td colSpan={cols} style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>No records found</td></tr>
);
const Loader = () => <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Loading…</div>;

function StatusBadge({ status }) {
  const C = { lead: ['#f1f5f9','#475569'], verified: ['#dbeafe','#1d4ed8'], visa_approved: ['#ede9fe','#6d28d9'], enrolled: ['#dcfce7','#166534'] };
  const [bg, text] = C[status] || ['#f1f5f9','#475569'];
  return <span style={{ background: bg, color: text, padding: '.25rem .7rem', borderRadius: 20, fontSize: '.78rem', fontWeight: 600, textTransform: 'capitalize' }}>{status?.replace('_',' ')}</span>;
}

function StageBadge({ stage }) {
  const C = { enrolled: ['#dcfce7','#166534'], visa_approved: ['#ede9fe','#6d28d9'], verified: ['#dbeafe','#1d4ed8'] };
  const [bg, text] = C[stage] || ['#f1f5f9','#475569'];
  return <span style={{ background: bg, color: text, padding: '.25rem .7rem', borderRadius: 20, fontSize: '.78rem', fontWeight: 600 }}>{stage?.replace('_',' ')}</span>;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  page:        { maxWidth: 1200, margin: '0 auto', padding: '2rem 1.25rem', fontFamily: "'Inter','Segoe UI',sans-serif" },
  pageHeader:  { marginBottom: '1.5rem' },
  pageTitle:   { fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', margin: 0 },
  pageSub:     { color: '#64748b', fontSize: '.9rem', marginTop: '.3rem' },
  errBox:      { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '.75rem 1rem', borderRadius: 8, marginBottom: '1rem' },
  okBox:       { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '.75rem 1rem', borderRadius: 8, marginBottom: '1rem' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' },
  summaryCard: { background: '#fff', borderRadius: 12, padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,.08)', borderLeft: '4px solid #ccc' },
  summaryVal:  { fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' },
  summaryLabel:{ fontSize: '.8rem', color: '#64748b', marginTop: '.25rem' },
  filterRow:   { display: 'flex', gap: '.75rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' },
  filterLabel: { fontSize: '.85rem', color: '#64748b', fontWeight: 600 },
  select:      { border: '1.5px solid #d1d5db', borderRadius: 8, padding: '.4rem .8rem', fontSize: '.9rem' },
  count:       { fontSize: '.85rem', color: '#94a3b8' },
  thead:       { background: '#f8fafc' },
  tr:          { borderBottom: '1px solid #f1f5f9' },
  small:       { color: '#64748b', fontSize: '.78rem' },
  badge:       { display: 'inline-block', padding: '.2rem .6rem', borderRadius: 20, fontSize: '.75rem', fontWeight: 600 },
  code:        { background: '#f1f5f9', padding: '.2rem .5rem', borderRadius: 4, fontSize: '.82rem', fontFamily: 'monospace' },
  doneLabel:   { fontSize: '.8rem', color: '#10b981', fontWeight: 600 },
  blueBtn:     { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '.35rem .8rem', fontSize: '.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  greenBtn:    { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, padding: '.35rem .8rem', fontSize: '.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  purpleBtn:   { background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, padding: '.35rem .8rem', fontSize: '.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  redBtn:      { background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, padding: '.35rem .8rem', fontSize: '.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  deleteBtn:   { background: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3', borderRadius: 6, padding: '.35rem .8rem', fontSize: '.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  searchInput: { padding: '.45rem .9rem', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '.9rem', color: '#1e293b', background: '#fff', outline: 'none', width: 280 },
  createBtn:   { padding: '.55rem 1.2rem', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '.88rem', cursor: 'pointer' },
  formCard:    { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,.05)' },
  formTitle:   { fontSize: '1.05rem', fontWeight: 700, color: '#1e293b', marginTop: 0, marginBottom: '1.25rem' },
  formRow:     { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' },
  field:       { display: 'flex', flexDirection: 'column' },
  label:       { fontSize: '.8rem', fontWeight: 600, color: '#374151', marginBottom: '.35rem' },
  input:       { padding: '.65rem .9rem', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '.9rem', color: '#1e293b', background: '#f8fafc', outline: 'none', width: '100%', boxSizing: 'border-box' },
  editBtn:     { background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, padding: '.35rem .8rem', fontSize: '.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  pwBtn:       { background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 6, padding: '.35rem .8rem', fontSize: '.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  cancelBtn:   { border: '1.5px solid #e2e8f0', background: '#fff', borderRadius: 7, padding: '.45rem 1.1rem', fontSize: '.88rem', fontWeight: 600, cursor: 'pointer', color: '#475569' },
  overlay:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal:       { background: '#fff', borderRadius: 12, padding: '2rem', width: '100%', maxWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,.18)' },
  modalTitle:  { margin: '0 0 1.25rem', fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' },
  modalFooter: { display: 'flex', gap: '.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' },
};
